import { type Collection, type List, Record, type RecordOf } from 'immutable';

import type { BaseResource, QcsSyncResourcesPayload } from '../core/resourceStoreTypes';

export const PREFERENCES_REDUCER_KEY = 'preferencesStore';
export type PREFERENCES_REDUCER_KEY = typeof PREFERENCES_REDUCER_KEY;

// QCS also has client and qcs sections; the store only holds the shared one.
export type PreferenceSection = 'shared';
export type PreferenceGroup = 'user' | 'dataset';
export type PreferencePath = {
  readonly section: PreferenceSection;
  readonly group: PreferenceGroup;
};

/** A preference document: the JSON object QCS stores for a group, or a part of it to merge. */
export type PreferenceDocument = { readonly [key: string]: unknown };

/*
 * The types below describe a group's document as the store holds it. QCS returns whatever clients
 * wrote; the reducer keeps objects as plain objects and turns every array into an Immutable List
 * (objects inside a List become Maps). They name the keys this app reads; any other key is unknown.
 */

export type AccountPreference = {
  readonly [key: string]: unknown;
  readonly color?: number;
};
export type AccountPreferencesById = { readonly [accountId: string]: AccountPreference | undefined };
export type AccountsPreferences = {
  readonly [key: string]: unknown;
  readonly byId?: AccountPreferencesById;
};
// 'clear' marks deleted account preferences (see initAccountPrefs).
export type AccountsPreferencesValue = AccountsPreferences | 'clear';

// Register column order by register type (cashFlow, investment, credit).
export type RegisterFieldsSortPreferences = { readonly [registerType: string]: List<string> | undefined };
export type TransactionRegisterPreferences = {
  readonly [key: string]: unknown;
  readonly registerComfort?: string;
  readonly regFieldsSort?: RegisterFieldsSortPreferences;
};

export type DatasetWebAppPreferences = {
  readonly [key: string]: unknown;
  readonly accountsPrefs?: AccountsPreferencesValue | null;
  readonly completedGSF?: boolean;
  readonly createCategoryRules?: boolean;
  readonly createPayeeRules?: boolean;
  readonly dashboardVersion?: number;
  readonly hidePlanned?: boolean;
  // mobile: true once the user has customized the dashboard
  readonly isCustomizeSelected?: boolean | null;
  readonly kioskmode?: boolean;
  readonly projectedOpen?: boolean;
  readonly setupChecklistDismissed?: boolean;
  readonly setupStateIndex?: number | null;
  readonly spendingPlanSetupCompleted?: boolean | null;
  readonly swlOrder?: string;
  readonly theme?: string | null;
  readonly transactionRegister?: TransactionRegisterPreferences;
  readonly watchlistSetupCompleted?: boolean | null;
};

export type DatasetA2aPreferences = {
  readonly [key: string]: unknown;
  readonly hideAllTransactionBanners?: boolean;
  readonly hideCard?: boolean;
};

export type DatasetPreferences = {
  readonly [key: string]: unknown;
  readonly a2a?: DatasetA2aPreferences;
  readonly dashboardTaskCardDismissed?: boolean;
  readonly dashboardTaskDismissedCards?: List<string>;
  // The store's defaults always include webApp.
  readonly webApp: DatasetWebAppPreferences;
};

export type UserA2aPreferences = {
  readonly [key: string]: unknown;
  readonly showIdentityReviewedCard?: boolean;
};

export type UserWebAppPreferences = {
  readonly [key: string]: unknown;
  readonly selectedDatasetId?: string;
};

export type UserPreferences = {
  readonly [key: string]: unknown;
  readonly a2a?: UserA2aPreferences;
  // ISO dates
  readonly annualConversionMessageDismissedOn?: string | null;
  readonly subscriptionMessageDismissedOn?: string | null;
  readonly webApp?: UserWebAppPreferences;
};

export type SharedPreferences = {
  readonly user: UserPreferences;
  readonly dataset: DatasetPreferences;
};

type PreferenceListItemInput<E> = E extends Collection<unknown, unknown> ? object : PreferenceInput<E>;

/**
 * What a write may send for a stored preference: any part of it, with lists as arrays or Immutable
 * collections. QCS gets the JSON, and the reducer stores lists as Lists either way.
 */
export type PreferenceInput<T> = unknown extends T
  ? unknown
  : T extends List<infer E>
    ? ReadonlyArray<PreferenceListItemInput<E>> | Collection.Indexed<PreferenceListItemInput<E>>
    : T extends string | number | boolean | null | undefined
      ? T
      : { readonly [K in keyof T]?: PreferenceInput<T[K]> };

export type UserPreferencesInput = PreferenceInput<UserPreferences>;
export type UserWebAppPreferencesInput = PreferenceInput<UserWebAppPreferences>;
export type DatasetPreferencesInput = PreferenceInput<DatasetPreferences>;
export type DatasetWebAppPreferencesInput = PreferenceInput<DatasetWebAppPreferences>;

export type SetPreferencePayload =
  | { readonly section: PreferenceSection; readonly group: 'user'; readonly preference: UserPreferencesInput }
  | { readonly section: PreferenceSection; readonly group: 'dataset'; readonly preference: DatasetPreferencesInput };

/** A fetched preference document and the group it was fetched for. */
export type PreferencesResponseProps = BaseResource & {
  path?: PreferencePath;
  data?: unknown;
};
export const mkPreferencesResponse = Record<PreferencesResponseProps>({
  path: undefined,
  data: undefined,
});
export type GetPreferencesSuccessPayload = QcsSyncResourcesPayload<PreferencesResponseProps>;

export type PreferencesLoadPending = {
  readonly shared: { readonly [G in PreferenceGroup]: boolean };
};

export type PreferencesProps = {
  shared: SharedPreferences;
};
export type Preferences = RecordOf<PreferencesProps>;
export const mkPreferences = Record<PreferencesProps>({
  shared: {
    user: {},
    dataset: {
      webApp: {},
    },
  },
});

export type PreferencesStoreProps = {
  isLoading: boolean | undefined;
  loadPending: PreferencesLoadPending;
  preferences: Preferences;
  error: unknown;
};
export type PreferencesStore = RecordOf<PreferencesStoreProps>;
export const mkPreferencesStore = Record<PreferencesStoreProps>({
  isLoading: undefined,
  loadPending: {
    shared: {
      user: true,
      dataset: true,
    },
  },
  preferences: mkPreferences(),
  error: undefined,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [PREFERENCES_REDUCER_KEY]: PreferencesStore;
  }
}
